import type { AgentMemoryWithBindings } from '@cortex/types';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import './memory.css';
import { useAuth } from '../../../auth/AuthProvider';
import { AgentMultiSelect } from '../../../components/common/AgentMultiSelect';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { CreateMemoryModal } from '../../../components/refinement-memory/CreateMemoryModal';
import { MemoryListRow } from '../../../components/refinement-memory/MemoryListRow';
import { bindingReachesAnyAgent, useCollectionIdsByAgent } from '../../../lib/agent-collection';
import {
  useBatchApproveMemories,
  useBatchRejectMemories,
  useMemoryList,
} from '../../../lib/agent-memory';
import { decodeJwt } from '../../../lib/jwt';

type Tab = 'approved' | 'pending' | 'rejected';

/**
 * /agents/memories — the Memories sub-page of the Agents nav folder;
 * the single canonical surface for all agent memories, against the
 * unified /v1/memories API (DEV-16962).
 *
 * Three status tabs map to the unified lifecycle:
 *   - Active    → status=APPROVED               (auto-injected into every conversation)
 *   - Pending   → status IN (PENDING, CANDIDATE) (agent-proposed, awaiting operator review)
 *   - Rejected  → status=REJECTED                (kept for audit, never injected)
 *
 * Both PENDING (plain agent-authored) and CANDIDATE (feedback-loop
 * proposal) are pre-APPROVED states an operator must act on — the unified
 * API has no operator-staged-pending path, so the Pending tab only ever
 * shows agent-originated rows.
 *
 * Within each tab, binding-model filters narrow the list (DEV-17001):
 *   - an agent multi-select → memories bound to any selected agent;
 *   - "Org-wide" → appliesToAll rows; "Mine" → the current user's userSubject
 *     rows (an orthogonal per-user filter, not a scope).
 * These replaced the legacy org/personal/workflow/agent scope dropdown — the
 * unified store has no such single-axis scope concept.
 *
 * The pending badge count is always live regardless of active tab so
 * operators can see items awaiting review without switching first.
 *
 * Deep-link support (from other pages' agent/workflow pills):
 *   /agents/memories?status=pending&agentId=agt_abc
 *   /agents/memories?status=pending&workflowId=wf_abc (still-theopolis-backed
 *   refinement/overview page; matched here via the subjectRef.workflowId
 *   within-set filter, not a binding — see the Workflow scope note above)
 *   /agents/memories?memoryId=mem_abc — ONE memory, resolved across every
 *   status tab (the orchestration graph's memory glyphs link here; the caller
 *   knows the id but not which review tab the row currently sits on)
 */
function MemoryPage() {
  const { token } = useAuth();
  const currentSub = useMemo(() => decodeJwt(token)?.sub ?? null, [token]);

  const initialSearch = Route.useSearch();
  const [tab, setTab] = useState<Tab>(initialSearch.status ?? 'approved');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  // Binding-model filters (DEV-17001), replacing the legacy org/personal/
  // workflow/agent scope dropdown (that single-axis model was retired with the
  // unified store). Independent (AND): the agent multi-select narrows to
  // memories bound to any selected agent; "Org-wide" → appliesToAll; "Mine" →
  // the current user's userSubject rows.
  const [agentFilterIds, setAgentFilterIds] = useState<string[]>([]);
  const [orgWideOnly, setOrgWideOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  // Type filter for judge-promoted principles (orchestrator reflection memory
  // v1.1) — client-side like the other filters; the dataset is org-scale.
  const [principlesOnly, setPrinciplesOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  // Batch review selection (DEV-16845) — pending tab only.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const batchApprove = useBatchApproveMemories();
  const batchReject = useBatchRejectMemories();
  const changeTab = (next: Tab) => {
    // Selection never survives a view change — a hidden-but-selected row
    // must not be batch-actioned.
    setSelected(new Set());
    setTab(next);
  };

  // Specific-id deep-link filters (sticky for the session; not updatable via UI).
  const initialAgentId = initialSearch.agentId ?? null;
  const initialWorkflowId = initialSearch.workflowId ?? null;
  const focusedMemoryId = initialSearch.memoryId ?? null;

  // All queries run unconditionally — the dataset is small (org-scale) so
  // fetching in parallel is cheaper than conditional-enable logic. Pending
  // review merges two statuses (PENDING + CANDIDATE) client-side since the
  // list endpoint filters by a single status.
  const approvedQuery = useMemoryList({ status: 'APPROVED', includeArchived });
  const pendingQuery = useMemoryList({ status: 'PENDING' });
  const candidateQuery = useMemoryList({ status: 'CANDIDATE' });
  const rejectedQuery = useMemoryList({ status: 'REJECTED', includeArchived });

  const pendingReviewData = useMemo<AgentMemoryWithBindings[]>(
    () => [...(pendingQuery.data ?? []), ...(candidateQuery.data ?? [])],
    [pendingQuery.data, candidateQuery.data],
  );

  const activeQuery =
    tab === 'approved' ? approvedQuery : tab === 'rejected' ? rejectedQuery : null;
  // A single-memory link reads across every status, so it is only settled once
  // all four queries are.
  const allQueries = [approvedQuery, pendingQuery, candidateQuery, rejectedQuery];
  const isLoading = focusedMemoryId
    ? allQueries.some((q) => q.isLoading)
    : tab === 'pending'
      ? pendingQuery.isLoading || candidateQuery.isLoading
      : (activeQuery?.isLoading ?? false);
  const isError = focusedMemoryId
    ? allQueries.some((q) => q.isError)
    : tab === 'pending'
      ? pendingQuery.isError || candidateQuery.isError
      : (activeQuery?.isError ?? false);
  const errorObj = focusedMemoryId
    ? allQueries.find((q) => q.error)?.error
    : tab === 'pending'
      ? (pendingQuery.error ?? candidateQuery.error)
      : activeQuery?.error;
  const activeData = activeQuery?.data;
  const data = useMemo<AgentMemoryWithBindings[]>(() => {
    // A single-memory link searches EVERY status: the caller (a graph glyph)
    // knows the id but not which review tab the row happens to live on, and
    // landing on an empty "approved" tab would look like the memory vanished.
    if (focusedMemoryId) {
      return [
        ...(approvedQuery.data ?? []),
        ...pendingReviewData,
        ...(rejectedQuery.data ?? []),
      ].filter((m) => m.id === focusedMemoryId);
    }
    return tab === 'pending' ? pendingReviewData : (activeData ?? []);
  }, [focusedMemoryId, approvedQuery.data, rejectedQuery.data, tab, pendingReviewData, activeData]);
  const pendingCount = pendingReviewData.length;

  // Collection membership of the filtered agents (multi-select + deep-link) so
  // agent filters match everything that actually reaches those agents —
  // appliesToAll ∪ direct ∪ collection-derived — consistent with the resolved
  // counts on the agent-detail page that deep-link here.
  const filteredAgentIds = useMemo(
    () => [...new Set([...agentFilterIds, ...(initialAgentId ? [initialAgentId] : [])])],
    [agentFilterIds, initialAgentId],
  );
  const collectionsByAgent = useCollectionIdsByAgent(filteredAgentIds);

  const filtered = useMemo<AgentMemoryWithBindings[]>(() => {
    const q = search.trim().toLowerCase();
    return data.filter((m) => {
      // Binding-model filters (independent, AND).
      if (orgWideOnly && !m.appliesToAll) return false;
      if (mineOnly && (!currentSub || m.userSubject !== currentSub)) return false;
      if (principlesOnly && m.type !== 'PRINCIPLE') return false;
      if (
        agentFilterIds.length > 0 &&
        !bindingReachesAnyAgent(m, agentFilterIds, collectionsByAgent)
      ) {
        return false;
      }
      // Deep-link specific-id (from other pages' pills) — same reach semantics.
      if (initialAgentId && !bindingReachesAnyAgent(m, [initialAgentId], collectionsByAgent)) {
        return false;
      }
      if (initialWorkflowId) {
        const ref = m.subjectRef as Record<string, unknown> | null;
        if (!ref || ref.workflowId !== initialWorkflowId) return false;
      }
      // Text search
      if (q && !m.content.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [
    data,
    orgWideOnly,
    mineOnly,
    principlesOnly,
    currentSub,
    initialAgentId,
    initialWorkflowId,
    search,
    agentFilterIds,
    collectionsByAgent,
  ]);

  const total = data.length;

  return (
    <div className="memory-page">
      <PageHeader
        title="Memories"
        actions={
          <>
            <Link to="/agents/memories/digest" className="btn btn--ghost">
              Digest
            </Link>
            <Link to="/agents/memories/metrics" className="btn btn--ghost">
              Metrics
            </Link>
            {tab === 'approved' ? (
              <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
                + New memory
              </button>
            ) : null}
          </>
        }
      />

      {initialAgentId || initialWorkflowId || focusedMemoryId ? (
        <DeepLinkBanner
          agentId={initialAgentId}
          workflowId={initialWorkflowId}
          memoryId={focusedMemoryId}
        />
      ) : null}

      <div className="memory-page__filters">
        <div className="memory-page__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'approved'}
            className={`btn btn--ghost btn--sm ${tab === 'approved' ? 'is-active' : ''}`}
            onClick={() => changeTab('approved')}
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'pending'}
            className={`btn btn--ghost btn--sm ${tab === 'pending' ? 'is-active' : ''}`}
            onClick={() => changeTab('pending')}
          >
            Pending review
            {pendingCount > 0 ? (
              <span className="badge badge--warning" style={{ marginLeft: 'var(--sp-1)' }}>
                {pendingCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'rejected'}
            className={`btn btn--ghost btn--sm ${tab === 'rejected' ? 'is-active' : ''}`}
            onClick={() => changeTab('rejected')}
          >
            Rejected
          </button>
        </div>

        <div className="memory-page__controls">
          <input
            type="search"
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search memory content…"
            style={{ minWidth: '200px' }}
          />
          {!initialAgentId && !initialWorkflowId ? (
            <>
              <div style={{ minWidth: '260px' }}>
                <AgentMultiSelect
                  selectedIds={agentFilterIds}
                  onChange={setAgentFilterIds}
                  placeholder="Filter by bound agent…"
                />
              </div>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--sp-1)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={orgWideOnly}
                  onChange={(e) => setOrgWideOnly(e.target.checked)}
                />
                <span className="t-xs">Org-wide</span>
              </label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--sp-1)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={(e) => setMineOnly(e.target.checked)}
                />
                <span className="t-xs">Mine</span>
              </label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--sp-1)',
                  cursor: 'pointer',
                }}
                title="Only judge-promoted principles — generalized lessons that guide future action"
              >
                <input
                  type="checkbox"
                  checked={principlesOnly}
                  onChange={(e) => setPrinciplesOnly(e.target.checked)}
                />
                <span className="t-xs">✦ Principles</span>
              </label>
            </>
          ) : null}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-1)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <span className="t-xs">Include archived</span>
          </label>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-6)' }}>
          <Spinner />
        </div>
      ) : isError ? (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)' }}>
          Failed to load memories: {errorObj instanceof Error ? errorObj.message : 'unknown error'}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={
            search || agentFilterIds.length > 0 || orgWideOnly || mineOnly
              ? 'No memories match these filters.'
              : tab === 'pending'
                ? 'No pending memories.'
                : tab === 'approved'
                  ? 'No active memories yet.'
                  : 'No rejected memories.'
          }
          description={
            tab === 'pending'
              ? 'When the agent proposes a memory mid-conversation, it lands here for review.'
              : tab === 'approved'
                ? 'Active memories auto-inject into every conversation. Create one above, or let the agent propose one.'
                : 'Rejected memories are kept for audit but never injected.'
          }
        />
      ) : (
        <>
          <p
            className="t-xs t-muted"
            style={{ marginTop: 'var(--sp-3)', marginBottom: 'var(--sp-2)' }}
          >
            Showing {filtered.length} of {total}
          </p>
          {tab === 'pending' && filtered.length > 1 ? (
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                alignItems: 'center',
                marginBottom: 'var(--sp-2)',
              }}
            >
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                <input
                  type="checkbox"
                  checked={selected.size === filtered.length && filtered.length > 0}
                  onChange={() =>
                    setSelected((prev) =>
                      prev.size === filtered.length
                        ? new Set()
                        : new Set(filtered.map((m) => m.id)),
                    )
                  }
                />
                <span className="t-xs">Select all</span>
              </label>
              {selected.size > 0 ? (
                <>
                  <span className="t-xs t-muted">{selected.size} selected</span>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={batchApprove.isPending}
                    onClick={() => {
                      // Intersect with what is VISIBLE — filters may have
                      // changed since selection; hidden rows are never acted on.
                      const visible = new Set(filtered.map((m) => m.id));
                      batchApprove.mutate([...selected].filter((id) => visible.has(id)));
                      setSelected(new Set());
                    }}
                  >
                    {batchApprove.isPending ? 'Approving…' : 'Approve selected'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={batchReject.isPending}
                    onClick={() => {
                      const visible = new Set(filtered.map((m) => m.id));
                      batchReject.mutate([...selected].filter((id) => visible.has(id)));
                      setSelected(new Set());
                    }}
                  >
                    {batchReject.isPending ? 'Rejecting…' : 'Reject selected'}
                  </button>
                </>
              ) : (
                <span className="t-xs t-muted">
                  Batch actions are admin-gated (fde role) — select rows to approve or reject as one
                  gesture.
                </span>
              )}
            </div>
          ) : null}
          {batchApprove.error || batchReject.error ? (
            <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-2)' }}>
              Batch action failed:{' '}
              {((batchApprove.error ?? batchReject.error) as Error | null)?.message}
            </div>
          ) : null}
          <ul className="refinement-memories-page__list">
            {filtered.map((m) => (
              <MemoryListRow
                key={m.id}
                memory={m}
                selected={tab === 'pending' ? selected.has(m.id) : undefined}
                onToggleSelect={
                  tab === 'pending'
                    ? () =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(m.id)) next.delete(m.id);
                          else next.add(m.id);
                          return next;
                        })
                    : undefined
                }
              />
            ))}
          </ul>
        </>
      )}

      {creating ? (
        <CreateMemoryModal
          onClose={() => setCreating(false)}
          onSuccess={() => {
            setTab('approved');
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}

function DeepLinkBanner({
  agentId,
  workflowId,
  memoryId,
}: {
  agentId: string | null;
  workflowId: string | null;
  memoryId: string | null;
}) {
  const target = memoryId
    ? `memory ${memoryId}`
    : agentId
      ? `agent ${agentId}`
      : `workflow ${workflowId}`;
  return (
    <div
      className="alert alert--info"
      style={{
        marginTop: 'var(--sp-3)',
        marginBottom: 'var(--sp-3)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
      }}
    >
      <span>
        Filtered to <code style={{ fontFamily: 'var(--font-mono)' }}>{target}</code>
      </span>
      <Link
        to="/agents/memories"
        search={{}}
        className="btn btn--ghost btn--xs"
        style={{ marginLeft: 'auto' }}
      >
        Clear filter
      </Link>
    </div>
  );
}

export interface IMemorySearch {
  status?: Tab;
  agentId?: string;
  workflowId?: string;
  /**
   * Address ONE memory (the orchestration graph's memory glyphs link here).
   * Narrows the list to that row across every status, so the link works
   * whether it is approved, awaiting review or rejected.
   */
  memoryId?: string;
}

export const Route = createFileRoute('/agents/memories/')({
  component: MemoryPage,
  validateSearch: (raw: Record<string, unknown>): IMemorySearch => {
    const status =
      raw.status === 'approved' || raw.status === 'pending' || raw.status === 'rejected'
        ? raw.status
        : undefined;
    return {
      status,
      agentId: typeof raw.agentId === 'string' ? raw.agentId : undefined,
      workflowId: typeof raw.workflowId === 'string' ? raw.workflowId : undefined,
      memoryId: typeof raw.memoryId === 'string' ? raw.memoryId : undefined,
    };
  },
});
